/**
 * Página de Relatório para Impressão/PDF
 * 
 * Renderiza as mesmas seções do dashboard em formato A4 otimizado para impressão.
 * O usuário pode:
 * 1. Selecionar quais seções incluir (Relatórios, Unidades, Documentos, Processos)
 * 2. Escolher tema claro ou escuro
 * 3. Visualizar o relatório em formato A4
 * 4. Gerar PDF via captura de screenshots com html2canvas + jsPDF
 */
import { useState, useRef, useEffect, Suspense, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { 
  ArrowLeft, 
  Eye, 
  CheckSquare,
  Square,
  FileText,
  Loader2,
  Sun,
  Moon,
  Building2,
  FolderOpen,
  ClipboardList,
  Smartphone,
  Monitor,
  X,
  RotateCcw,
  Download,
  Mail,
  FileDown,
  Check
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

// Importa as seções reais do relatório (para preview)
import {
  ImoveisSection,
  CenarioAvcbSection,
  CenarioAlvarasSection,
  DesempenhoSection,
} from '@/features/relatorios/components';

// Importa as páginas completas
import { UnidadesTab } from '@/features/unidades';
import { DocumentosTab } from '@/features/documentos';
import { ProcessosTab } from '@/features/processos';

// ============================================================================
// TIPOS
// ============================================================================

interface SectionConfig {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  component: React.ComponentType;
  enabled: boolean;
  category: 'relatorios' | 'paginas';
}

// ============================================================================
// WRAPPER PARA SEÇÕES - IGNORA ERROS DO RESIZE OBSERVER
// ============================================================================

function SectionWrapper({ 
  children, 
  onReady,
  sectionId
}: { 
  children: React.ReactNode; 
  onReady?: () => void;
  sectionId: string;
}) {
  const [hasError, setHasError] = useState(false);
  const readyCalledRef = useRef(false);

  useEffect(() => {
    // Evita chamar onReady múltiplas vezes
    if (readyCalledRef.current) return;

    // Aguarda um pouco para os gráficos renderizarem
    const timer = setTimeout(() => {
      if (!readyCalledRef.current) {
        readyCalledRef.current = true;
        console.log(`[Print] Seção ${sectionId} pronta`);
        onReady?.();
      }
    }, 1000); // Aumentado para 1 segundo para dar mais tempo aos gráficos

    return () => clearTimeout(timer);
  }, [onReady, sectionId]);

  // Captura erros de renderização
  if (hasError) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-700 dark:text-red-300">Erro ao carregar esta seção</p>
      </div>
    );
  }

  return <>{children}</>;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function RelatorioParaImpressao() {
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true); // Default landscape
  const [removedSections, setRemovedSections] = useState<Set<string>>(new Set());
  const [isContentReady, setIsContentReady] = useState(false);
  const [loadedSections, setLoadedSections] = useState<Set<string>>(new Set());
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');

  // Suprime erros do ResizeObserver do ECharts (são inofensivos mas poluem o console)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      // Ignora erros específicos do ResizeObserver
      if (args[0]?.toString?.().includes?.('ResizeObserver') ||
          args[0]?.toString?.().includes?.('disconnect') ||
          args[0]?.toString?.().includes?.('sensor is undefined')) {
        return;
      }
      originalError.apply(console, args);
    };

    // Handler global para erros não capturados
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('disconnect') || 
          event.message?.includes('sensor') ||
          event.message?.includes('ResizeObserver')) {
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener('error', handleError);

    return () => {
      console.error = originalError;
      window.removeEventListener('error', handleError);
    };
  }, []);

  // Sincroniza o tema com o document.documentElement para que os gráficos ECharts
  // possam detectar o tema corretamente via useIsDarkMode()
  useEffect(() => {
    if (!isPreviewMode) return;
    
    const html = document.documentElement;
    const originalHadDark = html.classList.contains('dark');
    
    if (isDarkMode) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
    
    // Restaura o estado original ao sair do preview
    return () => {
      if (originalHadDark) {
        html.classList.add('dark');
      } else {
        html.classList.remove('dark');
      }
    };
  }, [isDarkMode, isPreviewMode]);

  // Configuração inicial das seções
  const defaultSections: SectionConfig[] = [
    { 
      id: 'imoveis', 
      label: 'Imóveis', 
      description: 'Visão geral da carteira de imóveis por status e regional',
      icon: <Building2 className="h-4 w-4" />,
      component: ImoveisSection,
      enabled: true,
      category: 'relatorios'
    },
    { 
      id: 'cenario-avcb', 
      label: 'Cenário AVCB', 
      description: 'Auto de Vistoria do Corpo de Bombeiros - histórico e projeção',
      icon: <FileText className="h-4 w-4" />,
      component: CenarioAvcbSection,
      enabled: true,
      category: 'relatorios'
    },
    { 
      id: 'cenario-alvaras', 
      label: 'Cenário Alvarás', 
      description: 'Alvará de Funcionamento - histórico e evolução',
      icon: <FileText className="h-4 w-4" />,
      component: CenarioAlvarasSection,
      enabled: true,
      category: 'relatorios'
    },
    { 
      id: 'desempenho', 
      label: 'Desempenho', 
      description: 'Desempenho de processos por fornecedor',
      icon: <ClipboardList className="h-4 w-4" />,
      component: DesempenhoSection,
      enabled: true,
      category: 'relatorios'
    },
    // Páginas completas (não suportadas no PDF nativo por enquanto)
    { 
      id: 'unidades', 
      label: 'Unidades', 
      description: 'Lista completa de unidades/imóveis com filtros',
      icon: <Building2 className="h-4 w-4" />,
      component: UnidadesTab,
      enabled: false,
      category: 'paginas'
    },
    { 
      id: 'documentos', 
      label: 'Documentos', 
      description: 'Gestão de documentos e arquivos',
      icon: <FolderOpen className="h-4 w-4" />,
      component: DocumentosTab,
      enabled: false,
      category: 'paginas'
    },
    { 
      id: 'processos', 
      label: 'Processos', 
      description: 'Acompanhamento de processos de legalização',
      icon: <ClipboardList className="h-4 w-4" />,
      component: ProcessosTab,
      enabled: false,
      category: 'paginas'
    },
  ];
  
  // Seções disponíveis - Relatórios
  const [sections, setSections] = useState<SectionConfig[]>(defaultSections);

  // Toggle seção
  const toggleSection = (id: string) => {
    setSections(prev => 
      prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s)
    );
  };

  // Conta seções ativas
  const enabledCount = sections.filter(s => s.enabled).length;
  
  // Agrupa seções por categoria
  const relatoriosSections = sections.filter(s => s.category === 'relatorios');
  const paginasSections = sections.filter(s => s.category === 'paginas');

  // Remover seção do preview
  const removeSection = (id: string) => {
    setRemovedSections(prev => new Set([...prev, id]));
  };

  // Restaurar todas as seções
  const restoreAllSections = () => {
    setRemovedSections(new Set());
  };

  // Seções visíveis (habilitadas e não removidas)
  const visibleSections = sections.filter(s => s.enabled && !removedSections.has(s.id));

  // Callback quando uma seção termina de carregar
  const handleSectionReady = useCallback((sectionId: string) => {
    console.log(`[Print] handleSectionReady chamado para: ${sectionId}`);
    setLoadedSections(prev => {
      const newSet = new Set([...prev, sectionId]);
      console.log(`[Print] Seções carregadas: ${Array.from(newSet).join(', ')}`);
      return newSet;
    });
  }, []);

  // Verifica se todas as seções visíveis estão carregadas
  useEffect(() => {
    if (isPreviewMode && visibleSections.length > 0) {
      console.log(`[Print] Verificando: ${loadedSections.size}/${visibleSections.length} seções carregadas`);
      const allLoaded = visibleSections.every(s => loadedSections.has(s.id));
      
      if (allLoaded) {
        console.log('[Print] Todas as seções carregadas!');
        // Aguarda um pouco mais para os gráficos renderizarem completamente
        const timer = setTimeout(() => {
          console.log('[Print] Conteúdo pronto para impressão');
          setIsContentReady(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isPreviewMode, visibleSections, loadedSections]);

  // Fallback: Se após 10 segundos ainda não estiver pronto, força como pronto
  useEffect(() => {
    if (isPreviewMode && !isContentReady) {
      const fallbackTimer = setTimeout(() => {
        console.log('[Print] Fallback: Forçando conteúdo como pronto após timeout');
        setIsContentReady(true);
      }, 10000); // 10 segundos de fallback
      
      return () => clearTimeout(fallbackTimer);
    }
  }, [isPreviewMode, isContentReady]);

  // Reset quando sair do preview
  useEffect(() => {
    if (!isPreviewMode) {
      setIsContentReady(false);
      setLoadedSections(new Set());
    }
  }, [isPreviewMode]);

  // Gerar PDF - Captura seções em largura completa
  // Cada seção = 1 página do PDF com tamanho customizado (sem cortes)
  const handleGeneratePDF = async () => {
    if (!printRef.current) {
      alert('Erro: Conteúdo do relatório não encontrado');
      return;
    }
    
    setIsGenerating(true);
    setGenerationProgress('Preparando captura...');
    
    try {
      console.log('[PDF] Iniciando geração do PDF');
      
      // Encontra todas as seções
      const sectionElements = printRef.current.querySelectorAll('[data-section-id]');
      console.log(`[PDF] Encontradas ${sectionElements.length} seções`);
      
      if (sectionElements.length === 0) {
        throw new Error('Nenhuma seção encontrada para capturar');
      }
      
      // Aguarda renderização completa dos gráficos
      setGenerationProgress('Aguardando renderização dos gráficos...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Força resize dos gráficos ECharts
      window.dispatchEvent(new Event('resize'));
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Cria o PDF
      let pdfDoc: jsPDF | null = null;
      
      // Captura cada seção
      for (let i = 0; i < sectionElements.length; i++) {
        const sectionEl = sectionElements[i] as HTMLElement;
        const sectionId = sectionEl.getAttribute('data-section-id');
        
        // Pega a largura real do elemento (pode ser maior que 1920 se tiver conteúdo expandido)
        const captureWidth = Math.max(1920, sectionEl.scrollWidth);
        const captureHeight = sectionEl.scrollHeight;
        
        setGenerationProgress(`Capturando ${sectionId} (${i + 1}/${sectionElements.length})...`);
        console.log(`[PDF] Capturando seção: ${sectionId}, dimensões: ${captureWidth}x${captureHeight}`);
        
        try {
          // Captura com a largura total do conteúdo
          const canvas = await html2canvas(sectionEl, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
            logging: false,
            width: captureWidth,
            height: captureHeight,
            windowWidth: captureWidth,
            scrollX: 0,
            scrollY: 0,
          });
          
          // Converte para imagem
          const imgData = canvas.toDataURL('image/jpeg', 0.90);
          
          // Dimensões da página em mm
          // Escala proporcional: largura base de 400mm
          const pageWidthMm = 400;
          const aspectRatio = canvas.height / canvas.width;
          const pageHeightMm = pageWidthMm * aspectRatio;
          
          console.log(`[PDF] Seção ${sectionId}: ${canvas.width}x${canvas.height}px -> ${pageWidthMm}x${pageHeightMm.toFixed(0)}mm`);
          
          if (i === 0) {
            pdfDoc = new jsPDF({
              orientation: 'landscape',
              unit: 'mm',
              format: [pageWidthMm, pageHeightMm],
            });
          } else {
            pdfDoc!.addPage([pageWidthMm, pageHeightMm], 'landscape');
          }
          
          // Adiciona a imagem ocupando toda a página
          pdfDoc!.addImage(imgData, 'JPEG', 0, 0, pageWidthMm, pageHeightMm);
          
          console.log(`[PDF] Seção ${sectionId} adicionada ao PDF`);
          
        } catch (sectionError) {
          console.error(`[PDF] Erro ao capturar seção ${sectionId}:`, sectionError);
        }
      }
      
      if (!pdfDoc) {
        throw new Error('Nenhuma seção foi capturada');
      }
      
      setGenerationProgress('Finalizando PDF...');
      
      // Gera o blob
      const pdfBlob = pdfDoc.output('blob');
      
      console.log('[PDF] PDF gerado com sucesso, tamanho:', (pdfBlob.size / 1024 / 1024).toFixed(2), 'MB');
      setGenerationProgress('PDF gerado com sucesso!');
      
      // Baixa automaticamente
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-claro-gravity-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setPdfBlob(pdfBlob);
      setPdfGenerated(true);
      
    } catch (error) {
      console.error('[PDF] Erro ao gerar PDF:', error);
      alert(`Erro ao gerar o PDF: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Baixar PDF
  const handleDownloadPDF = () => {
    if (!pdfBlob) return;
    
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-claro-gravity-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Preparar email com PDF
  const handleEmailPDF = () => {
    if (!pdfBlob) return;
    
    // Abre o cliente de email padrão com assunto e corpo pré-preenchidos
    const subject = encodeURIComponent('Relatório Claro Gravity - ' + new Date().toLocaleDateString('pt-BR'));
    const body = encodeURIComponent(
      'Segue em anexo o relatório de conformidade do sistema Claro Gravity.\n\n' +
      'Gerado em: ' + new Date().toLocaleString('pt-BR') + '\n\n' +
      'Seções incluídas:\n' + visibleSections.map(s => '- ' + s.label).join('\n') + '\n\n' +
      'ATENÇÃO: O arquivo PDF deve ser anexado manualmente após o download.'
    );
    
    // Primeiro baixa o PDF
    handleDownloadPDF();
    
    // Depois abre o cliente de email
    setTimeout(() => {
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }, 500);
  };

  // Reset do PDF quando mudar configurações
  useEffect(() => {
    setPdfBlob(null);
    setPdfGenerated(false);
  }, [isDarkMode, isLandscape, visibleSections.length, removedSections]);

  // Voltar
  const handleBack = () => {
    if (isPreviewMode) {
      setIsPreviewMode(false);
    } else {
      navigate('/relatorio');
    }
  };

  // Componente de checkbox para seleção
  const SectionCheckbox = ({ section }: { section: SectionConfig }) => (
    <div
      onClick={() => toggleSection(section.id)}
      className={`
        flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all
        ${section.enabled 
          ? 'border-primary bg-primary/5 hover:bg-primary/10' 
          : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
        }
      `}
    >
      <div className="mt-0.5">
        {section.enabled ? (
          <CheckSquare className="h-5 w-5 text-primary" />
        ) : (
          <Square className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {section.icon}
          <span className="font-medium">{section.label}</span>
          {section.enabled && (
            <Badge variant="secondary" className="text-xs">Incluído</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {section.description}
        </p>
      </div>
    </div>
  );

  // ============================================================================
  // MODO CONFIGURAÇÃO
  // ============================================================================
  if (!isPreviewMode) {
    return (
      <div className="container max-w-4xl py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Gerar Relatório PDF</h1>
            <p className="text-muted-foreground">
              Selecione as seções que deseja incluir no relatório
            </p>
          </div>
        </div>

        {/* Configuração de Tema e Orientação */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isDarkMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                Tema do Relatório
              </CardTitle>
              <CardDescription>
                Escolha o tema visual para o PDF
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3">
                  <Sun className="h-5 w-5 text-amber-500" />
                  <span className="font-medium">Claro</span>
                </div>
                <Switch 
                  checked={isDarkMode}
                  onCheckedChange={setIsDarkMode}
                />
                <div className="flex items-center gap-3">
                  <span className="font-medium">Escuro</span>
                  <Moon className="h-5 w-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isLandscape ? <Monitor className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
                Orientação da Página
              </CardTitle>
              <CardDescription>
                Escolha a orientação do papel A4
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5 text-blue-500" />
                  <span className="font-medium">Retrato</span>
                </div>
                <Switch 
                  checked={isLandscape}
                  onCheckedChange={setIsLandscape}
                />
                <div className="flex items-center gap-3">
                  <span className="font-medium">Paisagem</span>
                  <Monitor className="h-5 w-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Seções de Relatórios */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Seções de Relatório
            </CardTitle>
            <CardDescription>
              Dashboards e análises do sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {relatoriosSections.map((section) => (
              <SectionCheckbox key={section.id} section={section} />
            ))}
          </CardContent>
        </Card>

        {/* Páginas do Sistema */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Páginas do Sistema
            </CardTitle>
            <CardDescription>
              Inclua tabelas e listagens completas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {paginasSections.map((section) => (
              <SectionCheckbox key={section.id} section={section} />
            ))}
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {enabledCount} seção(ões) selecionada(s)
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleBack}>
              Cancelar
            </Button>
            <Button 
              onClick={() => setIsPreviewMode(true)}
              disabled={enabledCount === 0}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              Visualizar Relatório
            </Button>
          </div>
        </div>

        {/* Dicas */}
        <Card className="mt-8 border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="pt-6">
            <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">
              💡 Como funciona
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• O relatório usa exatamente as mesmas visualizações do dashboard</li>
              <li>• Escolha entre tema claro ou escuro conforme preferência</li>
              <li>• Selecione orientação Retrato (vertical) ou Paisagem (horizontal)</li>
              <li>• Na tela de preview, você pode remover seções clicando no X</li>
              <li>• Clique em "Gerar PDF" para criar o documento</li>
              <li>• Após gerado, você pode baixar o PDF ou enviar por email</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // MODO PREVIEW / IMPRESSÃO
  // ============================================================================
  // Classes de tema para o preview
  const themeClasses = isDarkMode 
    ? 'print-theme-dark' 
    : 'print-theme-light';

  return (
    <>
      {/* Barra de controle (não imprime) */}
      <div className="print:hidden sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container max-w-7xl py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="font-semibold">Preview do Relatório</h2>
              <p className="text-sm text-muted-foreground">
                {visibleSections.length} seção(ões) • A4 {isLandscape ? 'Paisagem' : 'Retrato'} • {isDarkMode ? 'Escuro' : 'Claro'}
                {removedSections.size > 0 && (
                  <span className="text-orange-500"> • {removedSections.size} removida(s)</span>
                )}
                {!isContentReady && (
                  <span className="text-blue-500 ml-2">• Carregando...</span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="orientation-toggle" className="text-sm flex items-center gap-1">
                {isLandscape ? <Monitor className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
              </Label>
              <Switch 
                id="orientation-toggle"
                checked={isLandscape}
                onCheckedChange={setIsLandscape}
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="theme-toggle" className="text-sm">
                {isDarkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Label>
              <Switch 
                id="theme-toggle"
                checked={isDarkMode}
                onCheckedChange={setIsDarkMode}
              />
            </div>
            <Button variant="outline" onClick={() => setIsPreviewMode(false)}>
              Editar Seleção
            </Button>
            {removedSections.size > 0 && (
              <Button variant="outline" onClick={restoreAllSections} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Restaurar ({removedSections.size})
              </Button>
            )}
            
            {/* Botão de Gerar PDF */}
            {!pdfGenerated ? (
              <Button 
                onClick={handleGeneratePDF} 
                disabled={isGenerating || visibleSections.length === 0 || !isContentReady} 
                className="gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {generationProgress || 'Gerando...'}
                  </>
                ) : !isContentReady ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4" />
                    Gerar PDF
                  </>
                )}
              </Button>
            ) : (
              /* Botões de Download e Email após PDF gerado */
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30">
                  <Check className="h-3 w-3" />
                  PDF Pronto
                </Badge>
                <Button 
                  onClick={handleDownloadPDF} 
                  className="gap-2"
                  variant="default"
                >
                  <Download className="h-4 w-4" />
                  Baixar PDF
                </Button>
                <Button 
                  onClick={handleEmailPDF} 
                  variant="outline"
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Enviar por Email
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Indicador de carregamento */}
      {!isContentReady && (
        <div className="print:hidden fixed bottom-4 right-4 z-50">
          <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2 shadow-lg">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            <span className="text-blue-700 dark:text-blue-300 text-sm">
              Carregando gráficos ({loadedSections.size}/{visibleSections.length})
            </span>
          </div>
        </div>
      )}

      {/* Área de Preview */}
      <div className="print-area bg-muted/30 min-h-screen overflow-x-auto">
        <div 
          ref={printRef}
          id="print-content"
          className={`print-content ${themeClasses} ${isDarkMode ? 'dark' : ''}`}
          style={{ minWidth: '1920px', width: '1920px' }}
          data-orientation={isLandscape ? 'landscape' : 'portrait'}
          data-ready={isContentReady ? 'true' : 'false'}
        >
          {/* Capa do relatório */}
          <div className={`print-page print-cover ${isDarkMode ? 'dark' : ''}`}>
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="mb-8">
                <img 
                  src="/claro-logo.png" 
                  alt="Claro" 
                  className="h-16 mx-auto mb-4"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <h1 className="text-4xl font-bold mb-2 text-foreground">
                  Claro Gravity
                </h1>
                <p className="text-xl text-muted-foreground">
                  Sistema de Gestão de Legalizações
                </p>
              </div>
              
              <Separator className="w-32 my-8" />
              
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                Relatório de Conformidade
              </h2>
              
              <p className="mb-8 text-muted-foreground">
                {new Date().toLocaleDateString('pt-BR', { 
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              
              <div className="mt-8 text-sm text-muted-foreground">
                <p>Seções incluídas:</p>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {visibleSections.map(s => (
                    <Badge 
                      key={s.id} 
                      variant="outline"
                    >
                      {s.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Seções do relatório */}
          {visibleSections.map((section, index) => (
            <div 
              key={section.id} 
              id={`section-${section.id}`}
              className={`print-section ${isDarkMode ? 'dark' : ''}`}
              data-section-id={section.id}
            >
              {/* Cabeçalho da seção */}
              <div className="section-header mb-4 pb-2 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground">{section.icon}</span>
                    <span className="text-lg font-semibold text-foreground">
                      {section.label}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      — Seção {index + 1} de {visibleSections.length}
                    </span>
                  </div>
                  {/* Botão de remover (não aparece na impressão) */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="print:hidden text-muted-foreground hover:text-destructive"
                    onClick={() => removeSection(section.id)}
                    title="Remover esta seção do relatório"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Conteúdo da seção */}
              <div className="section-content">
                <SectionWrapper 
                  sectionId={section.id}
                  onReady={() => handleSectionReady(section.id)}
                >
                  <section.component />
                </SectionWrapper>
              </div>
            </div>
          ))}

          {/* Rodapé */}
          <div className={`print-footer-content ${isDarkMode ? 'dark' : ''}`}>
            <Separator className="mb-4" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Claro Gravity - Relatório de Conformidade</span>
              <span>Gerado em {new Date().toLocaleString('pt-BR')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos customizados */}
      <style>{`
        /* =============================================
           VARIÁVEIS CSS - TEMA CLARO
           ============================================= */
        .print-theme-light {
          --background: 0 0% 100%;
          --foreground: 222.2 84% 4.9%;
          --card: 0 0% 100%;
          --card-foreground: 222.2 84% 4.9%;
          --popover: 0 0% 100%;
          --popover-foreground: 222.2 84% 4.9%;
          --primary: 222.2 47.4% 11.2%;
          --primary-foreground: 210 40% 98%;
          --secondary: 210 40% 96.1%;
          --secondary-foreground: 222.2 47.4% 11.2%;
          --muted: 210 40% 96.1%;
          --muted-foreground: 215.4 16.3% 46.9%;
          --accent: 210 40% 96.1%;
          --accent-foreground: 222.2 47.4% 11.2%;
          --destructive: 0 84.2% 60.2%;
          --destructive-foreground: 210 40% 98%;
          --border: 214.3 31.8% 91.4%;
          --input: 214.3 31.8% 91.4%;
          --ring: 222.2 84% 4.9%;
        }
        
        /* =============================================
           VARIÁVEIS CSS - TEMA ESCURO
           ============================================= */
        .print-theme-dark,
        .print-theme-dark .dark {
          --background: 0 0% 11%;
          --foreground: 210 40% 98%;
          --card: 0 0% 14%;
          --card-foreground: 210 40% 98%;
          --popover: 0 0% 14%;
          --popover-foreground: 210 40% 98%;
          --primary: 210 40% 98%;
          --primary-foreground: 222.2 47.4% 11.2%;
          --secondary: 0 0% 18%;
          --secondary-foreground: 210 40% 98%;
          --muted: 0 0% 18%;
          --muted-foreground: 215 20.2% 65.1%;
          --accent: 0 0% 18%;
          --accent-foreground: 210 40% 98%;
          --destructive: 0 62.8% 30.6%;
          --destructive-foreground: 210 40% 98%;
          --border: 0 0% 22%;
          --input: 0 0% 22%;
          --ring: 212.7 26.8% 83.9%;
        }
        
        /* =============================================
           ESTILOS DE PREVIEW (TELA)
           ============================================= */
        @media screen {
          /* Portrait */
          .print-content.page-portrait {
            max-width: 210mm;
            margin: 20px auto;
            box-shadow: 0 0 20px rgba(0,0,0,0.15);
            border-radius: 4px;
            overflow: hidden;
          }
          
          /* Landscape */
          .print-content.page-landscape {
            max-width: 297mm;
            margin: 20px auto;
            box-shadow: 0 0 20px rgba(0,0,0,0.15);
            border-radius: 4px;
            overflow: hidden;
          }
          
          .print-content {
            transition: max-width 0.3s ease;
          }
          
          .print-page {
            min-height: 297mm;
            padding: 40px;
          }
          
          .print-section {
            padding: 30px 40px;
          }
          
          .print-footer-content {
            padding: 20px 40px;
          }
          
          /* Tema Claro - Tela */
          .print-theme-light {
            background: hsl(var(--background));
            color: hsl(var(--foreground));
          }
          
          .print-theme-light .print-page,
          .print-theme-light .print-section {
            background: hsl(var(--background));
            color: hsl(var(--foreground));
          }
          
          .print-theme-light .print-title {
            color: hsl(var(--foreground));
          }
          
          .print-theme-light .print-subtitle {
            color: hsl(var(--muted-foreground));
          }
          
          .print-theme-light .print-badge {
            border-color: hsl(var(--border));
            color: hsl(var(--foreground));
          }
          
          .print-theme-light .print-border {
            border-color: hsl(var(--border));
          }
          
          .print-theme-light .print-section {
            border-bottom: 1px solid hsl(var(--border));
          }
          
          /* Textos de gráficos - Tema Claro */
          .print-theme-light text,
          .print-theme-light .recharts-text,
          .print-theme-light .recharts-cartesian-axis-tick-value,
          .print-theme-light .recharts-label,
          .print-theme-light .recharts-legend-item-text,
          .print-theme-light .recharts-tooltip-label,
          .print-theme-light .recharts-pie-label-text,
          .print-theme-light tspan {
            fill: #111827 !important;
            color: #111827 !important;
          }
          
          /* ECharts - Tema Claro */
          .print-theme-light [class*="echarts"] text,
          .print-theme-light .echarts-for-react text,
          .print-theme-light canvas + div text {
            fill: #111827 !important;
          }
          
          .print-theme-light .recharts-cartesian-grid line {
            stroke: #e5e7eb !important;
          }
          
          /* Forçar cor escura em SVG text dentro do tema claro */
          .print-theme-light svg text {
            fill: #111827 !important;
          }
          
          .print-theme-light svg tspan {
            fill: #111827 !important;
          }
          
          /* Tema Escuro - Tela */
          .print-theme-dark {
            background: hsl(var(--background));
            color: hsl(var(--foreground));
          }
          
          .print-theme-dark .print-page,
          .print-theme-dark .print-section {
            background: hsl(var(--background));
            color: hsl(var(--foreground));
          }
          
          .print-theme-dark .print-title {
            color: hsl(var(--foreground));
          }
          
          .print-theme-dark .print-subtitle {
            color: hsl(var(--muted-foreground));
          }
          
          .print-theme-dark .print-badge {
            border-color: hsl(var(--border));
            color: hsl(var(--foreground));
          }
          
          .print-theme-dark .print-border {
            border-color: hsl(var(--border));
          }
          
          .print-theme-dark .print-section {
            border-bottom: 1px solid hsl(var(--border));
          }
          
          .print-theme-dark {
            box-shadow: 0 0 20px rgba(0,0,0,0.5);
          }
          
          /* Textos de gráficos - Tema Escuro */
          .print-theme-dark text,
          .print-theme-dark .recharts-text,
          .print-theme-dark .recharts-cartesian-axis-tick-value,
          .print-theme-dark .recharts-label,
          .print-theme-dark .recharts-legend-item-text,
          .print-theme-dark .recharts-tooltip-label,
          .print-theme-dark .recharts-pie-label-text,
          .print-theme-dark tspan {
            fill: #f3f4f6 !important;
            color: #f3f4f6 !important;
          }
          
          /* ECharts - Tema Escuro */
          .print-theme-dark [class*="echarts"] text,
          .print-theme-dark .echarts-for-react text,
          .print-theme-dark canvas + div text {
            fill: #f3f4f6 !important;
          }
          
          .print-theme-dark .recharts-cartesian-grid line {
            stroke: #374151 !important;
          }
          
          /* Forçar cor clara em SVG text dentro do tema escuro */
          .print-theme-dark svg text {
            fill: #f3f4f6 !important;
          }
          
          .print-theme-dark svg tspan {
            fill: #f3f4f6 !important;
          }
          
          /* PDF Generation styles - preview normal */
          .print-cover {
            min-height: 280mm;
          }
          
          .print-section {
            page-break-before: always;
          }
        }
        
        /* =============================================
           ESTILOS PARA GERAÇÃO DE PDF
           Layout fixo e compacto para A4
           ============================================= */
        .pdf-generating {
          /* Layout base para PDF */
          box-shadow: none !important;
          border-radius: 0 !important;
          background: ${isDarkMode ? '#1c1c1c' : '#ffffff'} !important;
          color: ${isDarkMode ? '#f3f4f6' : '#1f2937'} !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        
        .pdf-generating * {
          /* Força cores e visibilidade no PDF */
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          visibility: visible !important;
        }
        
        .pdf-generating .print-cover {
          min-height: 250mm !important;
          height: auto !important;
          padding: 40px !important;
          display: flex !important;
          flex-direction: column !important;
        }
        
        .pdf-generating .print-section {
          padding: 20px !important;
          display: block !important;
        }
        
        /* Gráficos ECharts */
        .pdf-generating .echarts-for-react {
          height: 280px !important;
          width: 100% !important;
        }
        
        /* Imagens de canvas substituídos */
        .pdf-generating img {
          max-width: 100% !important;
          height: auto !important;
        }
        
        /* Tabelas */
        .pdf-generating table {
          font-size: 11px !important;
          width: 100% !important;
        }
        
        .pdf-generating th,
        .pdf-generating td {
          padding: 6px 8px !important;
        }
        
        /* Cards */
        .pdf-generating .bg-card {
          padding: 10px !important;
        }
        
        /* Esconde apenas botões de ação */
        .pdf-generating button.print\\:hidden,
        .pdf-generating .print\\:hidden {
          display: none !important;
        }
        
        /* Textos ajustados */
        .pdf-generating .text-3xl {
          font-size: 1.5rem !important;
        }
        
        .pdf-generating .text-2xl {
          font-size: 1.25rem !important;
        }
        
        /* Overflow visible para tabelas */
        .pdf-generating .overflow-auto,
        .pdf-generating .overflow-x-auto {
          overflow: visible !important;
        }
      `}</style>
    </>
  );
}
